/**
 * Unifies the Poki and CrazyGames ad SDKs behind one interface so game code
 * doesn't need to branch on platform. Resolves to a no-op implementation on
 * web/itch/fb builds where neither SDK script is loaded.
 */

export interface PlatformSdk {
  init(): Promise<void>;
  gameLoadingFinished(): void;
  gameplayStart(): void;
  gameplayStop(): void;
  /** Shows an interstitial ad at a natural break (e.g. leaving a match). */
  requestMidrollAd(): Promise<void>;
  /** Shows a rewarded video ad. Resolves true only if it played to completion. */
  requestRewardedAd(): Promise<boolean>;
  /** Signals a positive moment (e.g. a win) to the platform's engagement tracking. */
  happytime(): void;
}

declare global {
  interface Window {
    PokiSDK?: {
      init(): Promise<void>;
      gameLoadingFinished(): void;
      gameplayStart(): void;
      gameplayStop(): void;
      commercialBreak(beforeAd?: () => void): Promise<void>;
      rewardedBreak(beforeAd?: () => void): Promise<boolean>;
    };
    CrazyGames?: {
      SDK: {
        init(): Promise<void>;
        game: {
          loadingStart(): void;
          loadingStop(): void;
          gameplayStart(): void;
          gameplayStop(): void;
          happytime(): void;
        };
        ad: {
          requestAd(
            type: 'midgame' | 'rewarded',
            callbacks: {
              adFinished: () => void;
              adError: (error: unknown) => void;
              adStarted?: () => void;
            },
          ): void;
        };
      };
    };
  }
}

// Third-party ad SDKs are out of our control — a rejected promise or thrown
// error from one must never block gameplay (e.g. leaving a match). Every
// external call is wrapped so it always resolves/returns, swallowing failures.
function safeSync(fn: (() => void) | undefined): void {
  try {
    fn?.();
  } catch {
    /* ignore — SDK call failures must not break the game */
  }
}

function safeAsync<T>(run: () => Promise<T> | undefined, fallback: T): Promise<T> {
  try {
    const result = run();
    if (!result) return Promise.resolve(fallback);
    return result.catch(() => fallback);
  } catch {
    return Promise.resolve(fallback);
  }
}

const noopSdk: PlatformSdk = {
  init: async () => {},
  gameLoadingFinished: () => {},
  gameplayStart: () => {},
  gameplayStop: () => {},
  requestMidrollAd: async () => {},
  requestRewardedAd: async () => false,
  happytime: () => {},
};

const pokiSdk: PlatformSdk = {
  init: () => safeAsync(() => window.PokiSDK?.init(), undefined),
  gameLoadingFinished: () => safeSync(() => window.PokiSDK?.gameLoadingFinished()),
  gameplayStart: () => safeSync(() => window.PokiSDK?.gameplayStart()),
  gameplayStop: () => safeSync(() => window.PokiSDK?.gameplayStop()),
  requestMidrollAd: () => safeAsync(() => window.PokiSDK?.commercialBreak(), undefined),
  requestRewardedAd: () => safeAsync(() => window.PokiSDK?.rewardedBreak(), false),
  // Poki has no equivalent engagement signal.
  happytime: () => {},
};

const crazyGamesSdk: PlatformSdk = {
  init: () => safeAsync(() => window.CrazyGames?.SDK.init(), undefined),
  gameLoadingFinished: () => safeSync(() => window.CrazyGames?.SDK.game.loadingStop()),
  gameplayStart: () => safeSync(() => window.CrazyGames?.SDK.game.gameplayStart()),
  gameplayStop: () => safeSync(() => window.CrazyGames?.SDK.game.gameplayStop()),
  requestMidrollAd: () =>
    safeAsync(
      () =>
        new Promise<void>((resolve) => {
          if (!window.CrazyGames) return resolve();
          window.CrazyGames.SDK.ad.requestAd('midgame', {
            adFinished: () => resolve(),
            adError: () => resolve(),
          });
        }),
      undefined,
    ),
  requestRewardedAd: () =>
    safeAsync(
      () =>
        new Promise<boolean>((resolve) => {
          if (!window.CrazyGames) return resolve(false);
          window.CrazyGames.SDK.ad.requestAd('rewarded', {
            adFinished: () => resolve(true),
            adError: () => resolve(false),
          });
        }),
      false,
    ),
  happytime: () => safeSync(() => window.CrazyGames?.SDK.game.happytime()),
};

export const platformSdk: PlatformSdk =
  import.meta.env.MODE === 'poki'
    ? pokiSdk
    : import.meta.env.MODE === 'crazygames'
      ? crazyGamesSdk
      : noopSdk;
